/**
 * Core Type Definitions for Maria's Tic-Tac-Toe
 * Based on ADR-003: Game State Management Architecture
 */

export type Player = 'X' | 'O';
export type CellValue = Player | null;
export type GameStatus = 'playing' | 'won' | 'draw';
export type Theme = 'default' | 'dark' | 'accessible';

/**
 * Central Game State Interface
 * Represents the complete state of a single game session
 */
export interface GameState {
  board: CellValue[][];
  currentPlayer: Player;
  gameStatus: GameStatus;
  winner: Player | null;
  winningCells: number[] | null; // Cell indices [0-8] for highlighting
}

/**
 * Player Profile Interface
 * Tracks statistics for a player across multiple games
 */
export interface PlayerProfile {
  name: string;
  wins: number;
  losses: number;
  draws: number;
}

/**
 * Player State Interface
 * Manages both player profiles
 */
export interface PlayerState {
  playerX: PlayerProfile;
  playerO: PlayerProfile;
  gameHistory: GameResult[];
}

/**
 * Game Result Interface
 * Records the outcome of a completed game
 */
export interface GameResult {
  gameId: string;
  winner: Player | 'draw';
  completedAt: Date;
  duration: number; // seconds
  moveHistory: Move[];
}

/**
 * Move Interface
 * Represents a single move in the game
 */
export interface Move {
  player: Player;
  row: number;
  col: number;
  timestamp: Date;
}

/**
 * Game Settings Interface
 * User preferences and configuration
 */
export interface GameSettings {
  theme: Theme;
  soundEnabled: boolean;
}

/**
 * Storage Keys
 * localStorage key constants
 */
export const STORAGE_KEYS = {
  PLAYERS: 'tictactoe_players',
  SETTINGS: 'tictactoe_settings',
  HISTORY: 'tictactoe_history',
} as const;

/**
 * Game Constants
 */
export const GAME_CONSTANTS = {
  BOARD_SIZE: 3,
  WIN_LENGTH: 3,
  MAX_HISTORY_ENTRIES: 50,
} as const;
